import { useState } from 'react';
import useWebData from '../../hooks/useWebData';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faArrowRight } from '@fortawesome/free-solid-svg-icons';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const Blog = () => {
  const { webData }: any = useWebData();
  document.title = webData?.companyName + ' | Blog';

  const blogPosts = [
    {
      id: 1,
      title: "Corporate Van Rentals: A Cost-Effective Solution for Employee Transport",
      summary: "In today's fast-paced business environment, ensuring reliable and efficient employee transportation is not just a matter of convenience - it's a strategic decision.",
      image: "../images/corporate-transport.jpg",
      date: "November 05, 2025",
      author: "James Smith",
      link: "/blog/corporate-transport"
    },
    {
      id: 2,
      title: "How to Coordinate Group Travel for Special Events with a Rental Van",
      summary: "Planning group travel for special events such as weddings, conferences, funerals, or corporate functions requires thoughtful coordination.",
      image: "../images/event-van-travel.jpg",
      date: "November 18, 2025",
      author: "William Brown",
      link: "/blog/event-van-travel"
    },
    {
      id: 3,
      title: "Airport Van Rentals What to Expect and How to Book Efficiently",
      summary: "Airport van rentals are an essential service for travelers who require a reliable and spacious mode of transport immediately upon arrival.",
      image: "../images/airport-van-travel.jpg",
      date: "November 27, 2025",
      author: "Oliver Taylor",
      link: "/blog/airport-van-travel"
    },
    {
      id: 4,
      title: "How Van Rentals Ensures a Comfortable Ride Every Time",
      summary: "Whether you're heading to a wedding, transporting employees to a corporate event, or planning a family getaway, comfort matters.",
      image: "../images/van-comfort.jpg",
      date: "December 10, 2025",
      author: "Harry Wilson",
      link: "/blog/van-comfort"
    },
    {
      id: 5,
      title: "How to Prepare for Your Van Rental Pickup",
      summary: "Picking up a rental van for your event, business need, or group trip should be a smooth and simple process. At JJ Van Rental, we want your journey to start right—with confidence and zero confusion.",
      image: "../images/van-pickup.jpg",
      date: "December 25, 2025",
      author: "George Thompson",
      link: "/blog/van-pickup"
    },
    {
      id: 6,
      title: "Making Group Travel Easier for Weddings and Family Functions",
      summary: "Weddings and family functions are joyful celebrations but coordinating travel for a large group can be overwhelming. From getting everyone to the venue on time to ensuring they travel comfortably, transportation plays a crucial role in making your special event run smoothly.",
      image: "../images/group-travel.jpg",
      date: "January 02, 2026",
      author: "Thomas Clark",
      link: "/blog/group-travel"
    },
    {
      id: 7,
      title: "The Ultimate Guide to Group Road Trips with a Van Rental",
      summary: "Planning a group road trip is exciting—but organizing transportation for everyone can be a challenge. That's where renting a van makes all the difference. Whether it's a friends getaway, family vacation, or corporate retreat, a rental van offers space, flexibility, and comfort for the entire group.",
      image: "../images/group-road-trip.jpg",
      date: "January 04, 2026",
      author: "Jack Johnson",
      link: "/blog/group-road-trip"
    },
    {
      id: 8,
      title: "How to Organize Transport for Large Conferences and Events",
      summary: "Planning transport for a large conference or event can feel overwhelming, but with the right approach, it can be smooth and efficient. Whether you're handling a corporate seminar, an industry trade show, or a multi-day summit, organizing transportation is key to guest satisfaction and overall event success.",
      image: "../images/conference-transport.jpg",
      date: "January 06, 2026",
      author: "Henry Walker",
      link: "/blog/conference-transport"
    }
  ];

  const postsPerPage = 6;
  const [currentPage, setCurrentPage] = useState(1);

  const sortedBlogPosts = [...blogPosts].sort((a, b) => b.id - a.id);
  const totalPages = Math.ceil(sortedBlogPosts.length / postsPerPage);

  const startIndex = (currentPage - 1) * postsPerPage;
  const endIndex = startIndex + postsPerPage;
  const currentPosts = sortedBlogPosts.slice(startIndex, endIndex);

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  return (
    <>
      <Helmet>
        <title>Vehicle & Van Rental Tips, Guides & Travel Insights | Blog</title>
        <meta
          name="description"
          content="Read our blog for expert tips, guides, and insights on vehicle and van rentals, travel planning, road trips, and rental best practices."
        />
      </Helmet>
      <div className="max-w-7xl mx-auto px-4 py-10 mt-[140px]">
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">Our Blog</h1>
          <p className="text-gray-600 text-lg md:text-xl">
            Tips, guides, and insights on car & van rentals, travel, and more.
          </p>
        </div>

        <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
          {currentPosts.map((post) => (
            <div
              key={post.id}
              className="bg-white rounded-xl shadow-md hover:shadow-lg transition duration-300 overflow-hidden"
            >
              <img
                src={post.image}
                alt={`${post.title} - vehicle rental blog`}
                className="w-full h-48 object-cover"
              />
              <div className="p-5">
                <p className="text-sm text-gray-500 mb-1">{post.date} | {post.author}</p>
                <h2 className="text-lg font-semibold text-gray-800 mb-2">{post.title}</h2>
                <p className="text-sm text-gray-600">{post.summary}</p>
                <Link
                  to={post.link}
                  className="text-primary hover:underline mt-3 inline-block text-sm font-medium"
                >
                  Read More →
                </Link>
              </div>
            </div>
          ))}
        </div>

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="flex justify-between items-center mt-12 px-4">
            <button
              onClick={goToPreviousPage}
              disabled={currentPage === 1}
              className={`font-semibold px-4 py-2 rounded ${currentPage === 1
                ? "text-gray-400 cursor-not-allowed"
                : "text-blue-500 hover:text-blue-700"
                }`}
            >
              <FontAwesomeIcon icon={faArrowLeft} /> <span>Previous Page</span>
            </button>

            <button
              onClick={goToNextPage}
              disabled={currentPage === totalPages}
              className={`font-semibold px-4 py-2 rounded ${currentPage === totalPages
                ? "text-gray-400 cursor-not-allowed"
                : "text-blue-500 hover:text-blue-700"
                }`}
            >
              Next Page <FontAwesomeIcon icon={faArrowRight} />
            </button>
          </div>
        )}
      </div>
    </>

  );
};

export default Blog;